import React, { useEffect, useState, useCallback } from 'react';
import articles from './ArticlesData';
import '../styles/ArticleGallery.css';

const ArticleGallery = () => {
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
  const [selectedPdfPath, setSelectedPdfPath] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // --- LOGIQUE DES DATES ---
  const stringToDate = useCallback((dateStr) => {
    if (!dateStr) return new Date();
    const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
    const parts = dateStr.toLowerCase().trim().split(' ');
    
    // Extraction intelligente du jour, mois, année
    const year = parseInt(parts.find(p => p.length === 4 && !isNaN(p)), 10);
    const monthIndex = months.findIndex(m => parts.includes(m));
    const day = parseInt(parts.find(p => !isNaN(p) && p.length <= 2), 10);
    
    return new Date(year, monthIndex, day);
  }, []);

  const parseDatesRange = useCallback((dateRangeStr) => {
    const lowerStr = dateRangeStr.toLowerCase();
    if (lowerStr.includes(' au ')) {
      const segments = lowerStr.split(' au ');
      const startDate = stringToDate(segments[0]);
      const endDate = stringToDate(segments[1]);
      
      // Sécurité année manquante sur le début de plage
      if (isNaN(startDate.getFullYear()) || startDate.getFullYear() < 2000) {
        startDate.setFullYear(endDate.getFullYear());
      }
      return { start: startDate, end: endDate };
    }
    const singleDate = stringToDate(dateRangeStr);
    return { start: singleDate, end: singleDate };
  }, [stringToDate]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const processed = articles.map(article => ({
      ...article,
      parsed: parseDatesRange(article.date)
    }));

    // FILTRE: Visible jusqu'à la fin de l'event
    // TRI: Chronologique (3 fév avant 12 mars)
    const active = processed
      .filter(article => {
        const end = new Date(article.parsed.end);
        end.setHours(23, 59, 59, 999);
        return end >= today;
      })
      .sort((a, b) => a.parsed.start - b.parsed.start);

    setFilteredArticles(active);

    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, [parseDatesRange]);

  // --- LOGIQUE UI (Identique à votre original) ---
  const scrollNews = (direction) => {
    const newsGrid = document.querySelector('.news-grid');
    const itemWidth = 300 + 24;
    if (newsGrid) {
      newsGrid.scrollBy({ left: direction * itemWidth, behavior: 'smooth' });
    }
  };

  const openPdfViewer = (pdfPath) => {
    if (isMobile) {
      window.open(pdfPath, '_blank');
    } else {
      setSelectedPdfPath(pdfPath);
      setPdfViewerVisible(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const closePdfViewer = () => {
    setPdfViewerVisible(false);
    setSelectedPdfPath(null);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && pdfViewerVisible) closePdfViewer();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [pdfViewerVisible]);

  const downloadPdf = () => {
    if (selectedPdfPath) {
      const link = document.createElement('a');
      link.href = selectedPdfPath;
      link.download = selectedPdfPath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <section className="news-section fade-in">
      <h2>Actualités du Musée et de l'Association</h2>
      <div className="news-grid-container">
        <button className="arrow left-arrow" onClick={() => scrollNews(-1)} aria-label="Article précédent">&#10094;</button>
        <div className="news-grid">
          {filteredArticles.map(article => (
            <div key={article.id} className="news-item">
              <img src={article.image} alt={article.title} />
              <h3>{article.title}</h3>
              <p>{article.date}</p>
              <p>{article.description}</p>
              <button className="btn btn-primary" onClick={() => openPdfViewer(article.pdfPath)}>Lire l'article</button>
            </div>
          ))}
        </div>
        <button className="arrow right-arrow" onClick={() => scrollNews(1)} aria-label="Article suivant">&#10095;</button>
      </div>

      {/* Rétablissement de votre structure originale de PDF Viewer */}
      {!isMobile && pdfViewerVisible && selectedPdfPath && (
        <div className="pdf-viewer-overlay" onClick={closePdfViewer}>
          <div className="pdf-viewer-container" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <button className="pdf-download-btn" onClick={downloadPdf}>Télécharger</button>
              <button className="close-pdf" onClick={closePdfViewer} aria-label="Fermer">&times;</button>
            </div>
            <div className="pdf-content">
              <iframe
                src={selectedPdfPath}
                className="pdf-viewer"
                title="Visualiseur PDF"
                key={selectedPdfPath}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ArticleGallery;